/**
 * Focal Area Positioning Algorithm
 *
 * Calculates the crop position to show the focal area in the viewport.
 * The marker is always positioned at MARKER_DEFAULT_SCREEN_X_PX from the left edge
 * and vertically centered — so the marker is always visible on load and there is
 * more context to the right of the annotation.
 *
 * Fallback hierarchy (when no marker exists):
 * - Paired image marker used as focal center
 * - Top-left of image
 */

/** Distance in pixels from the left edge of the container to the marker */
export const MARKER_DEFAULT_SCREEN_X_PX = 100;

export interface FocalAreaViewport {
  panX: number;
  panY: number;
  zoom: number;
}

export interface FocalAreaMarker {
  x: number;
  y: number;
}

export interface FocalAreaParams {
  viewport?: FocalAreaViewport | null;
  marker?: FocalAreaMarker | null;
  containerWidth: number;
  containerHeight: number;
  scale: number;
  /** Optional fallback focal center from the other image (for before/after pairs) */
  fallbackFocalCenter?: { x: number; y: number } | null;
}

export interface FocalAreaResult {
  cropX: number;
  cropY: number;
  /** The focal center point used (useful for sharing with paired image) */
  focalCenter?: { x: number; y: number };
}

/**
 * Calculate the crop position using the focal area algorithm.
 *
 * Priority: marker position always wins. The marker is placed at
 * MARKER_DEFAULT_SCREEN_X_PX from the left and vertically centered.
 * viewport.panX/panY is ignored when a marker is present.
 *
 * Fallback hierarchy:
 * 1. Has marker: marker at MARKER_DEFAULT_SCREEN_X_PX from left, vertically centered
 * 2. Has fallback focal center from paired image: same positioning
 * 3. Only viewport: use panX/panY as top-left corner
 * 4. Neither: start from top-left (0, 0)
 */
export function calculateFocalAreaCrop(params: FocalAreaParams): FocalAreaResult {
  const { viewport, marker, containerWidth, containerHeight, scale, fallbackFocalCenter } = params;

  // Check for valid viewport data
  const hasViewport = viewport &&
    typeof viewport.panX === 'number' &&
    typeof viewport.panY === 'number';

  // Check for valid marker data (must have positive coordinates)
  const hasMarker = marker && (marker.x > 0 || marker.y > 0);

  // Case 1: Has marker — always position marker at fixed offset from left, vertically centered
  // This ensures the marker is always visible on load regardless of saved viewport data.
  if (hasMarker) {
    const markerX = marker!.x;
    const markerY = marker!.y;
    const cropX = (markerX * scale) - MARKER_DEFAULT_SCREEN_X_PX;
    const cropY = (markerY * scale) - (containerHeight / 2);
    return { cropX, cropY, focalCenter: { x: markerX, y: markerY } };
  }

  // Case 2: No marker, but has fallback focal center from paired image
  if (fallbackFocalCenter && (fallbackFocalCenter.x > 0 || fallbackFocalCenter.y > 0)) {
    const cropX = (fallbackFocalCenter.x * scale) - MARKER_DEFAULT_SCREEN_X_PX;
    const cropY = (fallbackFocalCenter.y * scale) - (containerHeight / 2);
    return { cropX, cropY, focalCenter: fallbackFocalCenter };
  }

  // Case 3: Only viewport — use panX/panY as top-left corner
  if (hasViewport) {
    return {
      cropX: viewport!.panX * scale,
      cropY: viewport!.panY * scale,
    };
  }

  // Case 4: Neither — start from top-left
  return { cropX: 0, cropY: 0 };
}

/**
 * Clamp crop values to keep the image visible within the container.
 * Prevents showing empty space outside the image bounds.
 */
export function clampCropValues(
  cropX: number,
  cropY: number,
  scaledWidth: number,
  scaledHeight: number,
  containerWidth: number,
  containerHeight: number
): { cropX: number; cropY: number } {
  const maxCropX = Math.max(0, scaledWidth - containerWidth);
  const maxCropY = Math.max(0, scaledHeight - containerHeight);

  return {
    cropX: Math.max(0, Math.min(cropX, maxCropX)),
    cropY: Math.max(0, Math.min(cropY, maxCropY)),
  };
}
